import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, getDocs, query, orderBy, limit, addDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { Container, Paper, Typography, List, ListItem, Box, Avatar, Divider, Button, TextField, Grid, InputAdornment, Alert, Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, Chip, IconButton, Tooltip } from '@mui/material';
import { ArrowBack, AccountBalance, ArrowUpward, ArrowDownward, Add, History, Edit, Delete } from '@mui/icons-material';

function BancoDetalhes({ user }) {
  const { bancoId } = useParams();
  const navigate = useNavigate();
  const [movimentacoes, setMovimentacoes] = useState([]);
  const [banco, setBanco] = useState(null);
  const [despesas, setDespesas] = useState([]);
  
  // Estados para nova movimentação
  const [novaMovimentacao, setNovaMovimentacao] = useState({ titulo: '', valor: '', data: '', tipo: 'entrada', descricao: '', categoriaId: '' });
  const [openNovaMov, setOpenNovaMov] = useState(false);
  const [mensagem, setMensagem] = useState({ tipo: '', texto: '' });
  
  // Estados para edição
  const [openEdit, setOpenEdit] = useState(false);
  const [movSelecionada, setMovSelecionada] = useState(null);
  const [editTitulo, setEditTitulo] = useState('');
  const [editValor, setEditValor] = useState('');
  const [editData, setEditData] = useState('');
  const [editDescricao, setEditDescricao] = useState('');
  
  // Estados para editar nome do banco
  const [openEditBanco, setOpenEditBanco] = useState(false);
  const [novoNomeBanco, setNovoNomeBanco] = useState('');

  // Estado para confirmar exclusão do banco
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);

  const fetchDados = async () => {
    // Fetch banco details
    const bSnap = await getDoc(doc(db, `users/${user.uid}/bancos/${bancoId}`));
    setBanco(bSnap.data());

    // Fetch movimentacoes
    const qMov = query(collection(db, `users/${user.uid}/bancos/${bancoId}/movimentacoes`), orderBy('data', 'desc'), limit(50));
    const mSnap = await getDocs(qMov);
    setMovimentacoes(mSnap.docs.map(d => ({ id: d.id, ...d.data() })));

    // Fetch despesas for the category selector
    const qDespesas = query(collection(db, `users/${user.uid}/gastos_mensais`), orderBy('nome', 'asc'));
    const dSnap = await getDocs(qDespesas);
    setDespesas(dSnap.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  useEffect(() => {
    fetchDados();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.uid, bancoId]);

  const formatDateSafe = (value) => {
    if (!value) return 'Data não informada';
    let d = value;
    if (value?.toDate) d = value.toDate();
    if (typeof d === 'string') {
      d = new Date(d.includes('T') ? d : `${d}T12:00:00`);
    }
    if (d instanceof Date && !isNaN(d.getTime())) {
      return d.toLocaleDateString('pt-BR');
    }
    return 'Data não informada';
  };

  // Máscara de moeda
  const formatarMoeda = (value) => {
    let v = value.toString().replace(/\D/g, "");
    const res = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2 }).format(parseFloat(v) / 100);
    return res === "NaN" ? "" : res;
  };

  // Adicionar movimentação
  const adicionarMovimentacao = async () => {
    setMensagem({ tipo: '', texto: '' });

    if (!novaMovimentacao.titulo.trim()) {
      setMensagem({ tipo: 'error', texto: 'Descrição é obrigatória' });
      return;
    }

    if (!novaMovimentacao.valor) {
      setMensagem({ tipo: 'error', texto: 'Valor é obrigatório' });
      return;
    }

    if (!novaMovimentacao.data) {
      setMensagem({ tipo: 'error', texto: 'Data é obrigatória' });
      return;
    }

    let valor = parseFloat(novaMovimentacao.valor.replace(/\./g, '').replace(',', '.'));
    
    if (isNaN(valor) || valor === 0) {
      setMensagem({ tipo: 'error', texto: 'Valor inválido' });
      return;
    }

    // Se for saída, valor fica negativo
    if (novaMovimentacao.tipo === 'saida') {
      valor = -valor;
    }

    try {
      // Adiciona movimentação
      await addDoc(collection(db, `users/${user.uid}/bancos/${bancoId}/movimentacoes`), {
        titulo: novaMovimentacao.titulo.trim(),
        valor: valor,
        data: novaMovimentacao.data,
        tipo: novaMovimentacao.tipo,
        descricao: novaMovimentacao.descricao.trim()
      });

      // Atualiza saldo do banco
      const novoSaldo = banco.saldo + valor;
      await updateDoc(doc(db, `users/${user.uid}/bancos/${bancoId}`), { saldo: novoSaldo });

      // Limpa formulário e mostra mensagem de sucesso
      setNovaMovimentacao({ titulo: '', valor: '', data: '', tipo: 'entrada', descricao: '', categoriaId: '' });
      setMensagem({ tipo: 'success', texto: 'Movimentação adicionada com sucesso!' });
      setOpenNovaMov(false);
      
      // Limpa mensagem após 3 segundos
      setTimeout(() => setMensagem({ tipo: '', texto: '' }), 3000);
      
      // Recarrega dados
      fetchDados();
    } catch (error) {
      console.error("Erro ao adicionar movimentação:", error);
      setMensagem({ tipo: 'error', texto: 'Erro ao salvar movimentação. Tente novamente.' });
    }
  };

  const abrirNovaMov = () => {
    setMensagem({ tipo: '', texto: '' });
    setOpenNovaMov(true);
  };

  const fecharNovaMov = () => {
    setNovaMovimentacao({ titulo: '', valor: '', data: '', tipo: 'entrada', descricao: '', categoriaId: '' });
    setOpenNovaMov(false);
  };

  // Abrir edição
  const abrirEdicao = (movimentacao) => {
    setMovSelecionada(movimentacao);
    setEditTitulo(movimentacao.titulo);
    setEditValor(formatarMoeda(Math.abs(movimentacao.valor).toString()));
    setEditData(movimentacao.data);
    setEditDescricao(movimentacao.descricao || '');
    setOpenEdit(true);
  };

  // Salvar edição
  const salvarEdicao = async () => {
    if (!editTitulo.trim() || !editValor || !editData) {
      alert("Preencha todos os campos obrigatórios");
      return;
    }

    let novoValor = parseFloat(editValor.replace(/\./g, '').replace(',', '.'));
    
    if (movSelecionada.valor < 0) {
      novoValor = -novoValor;
    }

    const diferenca = novoValor - movSelecionada.valor;

    try {
      await updateDoc(doc(db, `users/${user.uid}/bancos/${bancoId}/movimentacoes/${movSelecionada.id}`), {
        titulo: editTitulo.trim(),
        valor: novoValor,
        data: editData,
        descricao: editDescricao.trim()
      });

      const novoSaldo = banco.saldo + diferenca;
      await updateDoc(doc(db, `users/${user.uid}/bancos/${bancoId}`), { saldo: novoSaldo });

      setOpenEdit(false);
      setMovSelecionada(null);
      setMensagem({ tipo: 'success', texto: 'Movimentação atualizada!' });
      setTimeout(() => setMensagem({ tipo: '', texto: '' }), 3000);
      fetchDados();
    } catch (error) {
      console.error("Erro ao atualizar:", error);
      alert("Erro ao atualizar movimentação");
    }
  };

  // Deletar movimentação
  const deletarMovimentacao = async (movimentacao) => {
    if (!window.confirm("Deseja excluir esta movimentação? Essa ação também irá reverter o status de pagamento da despesa vinculada, se houver.")) return;

    try {
      // Reverter o status da despesa, se vinculada
      if (movimentacao.gastoId) {
        const gastoRef = doc(db, `users/${user.uid}/gastos_mensais`, movimentacao.gastoId);
        const gastoSnap = await getDoc(gastoRef);
        if (gastoSnap.exists()) {
          const gasto = gastoSnap.data();
          
          // Se for parcela
          if (movimentacao.parcelaIndex !== undefined) {
            const novasParcelas = [...gasto.parcelas];
            novasParcelas[movimentacao.parcelaIndex].paga = false;
            novasParcelas[movimentacao.parcelaIndex].dataPagamento = null;
            
            const valorPago = novasParcelas.reduce((acc, p) => p.paga ? acc + p.valor : acc, 0);
            const parcelasPagasCount = novasParcelas.filter(p => p.paga).length;

            let novoStatus = 'pendente';
            if (parcelasPagasCount === novasParcelas.length) novoStatus = 'pago';
            else if (parcelasPagasCount > 0) novoStatus = 'parcial';

            await updateDoc(gastoRef, {
              parcelas: novasParcelas,
              status: novoStatus,
              valorPago: valorPago
            });

          } else { // Se for gasto único
            await updateDoc(gastoRef, { 
              status: 'pendente',
              valorPago: 0,
              dataPagamento: null
            });
          }
        }
      }

      // Deletar a movimentação do banco
      await deleteDoc(doc(db, `users/${user.uid}/bancos/${bancoId}/movimentacoes/${movimentacao.id}`));

      // Atualizar o saldo do banco (somando o valor de volta)
      const novoSaldo = banco.saldo - movimentacao.valor;
      await updateDoc(doc(db, `users/${user.uid}/bancos/${bancoId}`), { saldo: novoSaldo });

      setOpenEdit(false);
      setMovSelecionada(null);
      setMensagem({ tipo: 'success', texto: 'Movimentação excluída e despesa revertida!' });
      setTimeout(() => setMensagem({ tipo: '', texto: '' }), 3000);
      fetchDados(); // Recarrega os dados do banco
    } catch (error) {
      console.error("Erro ao deletar:", error);
      alert("Erro ao excluir movimentação");
    }
  };
  // Salvar edição do nome do banco
  const salvarEdicaoBanco = async () => {
    if (!novoNomeBanco.trim()) return;

    try {
      await updateDoc(doc(db, `users/${user.uid}/bancos/${bancoId}`), { nome: novoNomeBanco });
      setOpenEditBanco(false);
      setMensagem({ tipo: 'success', texto: 'Nome da conta atualizado!' });
      setTimeout(() => setMensagem({ tipo: '', texto: '' }), 3000);
      fetchDados();
    } catch (error) {
      console.error("Erro ao atualizar nome do banco:", error);
      setMensagem({ tipo: 'error', texto: 'Erro ao atualizar nome da conta' });
      setTimeout(() => setMensagem({ tipo: '', texto: '' }), 3000);
    }
  };

  const handleDeleteBanco = async () => {
    setOpenDeleteConfirm(true);
  };

  const confirmDeleteBanco = async () => {
    try {
      const movsQuery = query(collection(db, `users/${user.uid}/bancos/${bancoId}/movimentacoes`));
      const movsSnap = await getDocs(movsQuery);
      const deletePromises = [];
      movsSnap.forEach((docSnapshot) => {
        deletePromises.push(deleteDoc(docSnapshot.ref));
      });
      await Promise.all(deletePromises);

      await deleteDoc(doc(db, `users/${user.uid}/bancos/${bancoId}`));
      
      setMensagem({ tipo: 'success', texto: 'Conta bancária excluída com sucesso!' });
      setTimeout(() => {
        setMensagem({ tipo: '', texto: '' });
        navigate('/bancos');
      }, 2000);
    } catch (error) {
      console.error("Erro ao excluir conta bancária:", error);
      setMensagem({ tipo: 'error', texto: 'Erro ao excluir a conta. Tente novamente.' });
      setTimeout(() => setMensagem({ tipo: '', texto: '' }), 3000);
    }
    setOpenDeleteConfirm(false);
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 1, px: { xs: 0.5, sm: 2 } }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/bancos')} sx={{ mb: 1, fontWeight: 'bold', fontSize: { xs: '0.8rem', sm: '0.95rem' } }}>Voltar</Button>
      
      <Paper elevation={0} sx={{ p: 1.5, mb: 1.5, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: '1px solid #e0e0e0', flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ width: 20, height: 20, bgcolor: '#1a237e' }}><AccountBalance sx={{ fontSize: 12 }} /></Avatar>
          <Typography variant="body2" fontWeight="bold" sx={{ fontSize: { xs: '0.85rem', sm: '1rem' } }}>{banco?.nome}</Typography>
          <IconButton 
            size="small" 
            onClick={() => { setNovoNomeBanco(banco?.nome || ''); setOpenEditBanco(true); }} 
            sx={{ ml: -1, p: 0.5 }}
          >
            <Edit fontSize="small" />
          </IconButton>
          <IconButton 
            size="small" 
            onClick={handleDeleteBanco}
            sx={{ p: 0.5 }}
          >
            <Delete fontSize="small" />
          </IconButton>
        </Box>
        <Typography variant="body2" fontWeight="bold" color={banco?.saldo >= 0 ? 'green' : 'red'} sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>R$ {banco?.saldo?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</Typography>
      </Paper>

      {/* MENSAGEM DE FEEDBACK */}
      {mensagem.texto && (
        <Alert 
          severity={mensagem.tipo} 
          sx={{ mb: 1.5, fontSize: { xs: '0.8rem', sm: '0.9rem' } }}
          onClose={() => setMensagem({ tipo: '', texto: '' })}
        >
          {mensagem.texto}
        </Alert>
      )}

      {/* AÇÃO PARA ABRIR POPUP DE NOVA MOVIMENTAÇÃO */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1, mb: 1 }}>
        <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
          <History fontSize="small" /> Histórico
        </Typography>
        <Tooltip title="Nova movimentação" arrow>
          <IconButton
            size="medium"
            onClick={abrirNovaMov}
            sx={{ 
              bgcolor: '#1a237e', 
              color: '#fff',
              px: 2,
              height: 40,
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(26,35,126,0.25)',
              '&:hover': { bgcolor: '#0d1440' } 
            }}
          >
            <Typography variant="button" sx={{ fontWeight: 'bold', textTransform: 'none' }}>
              Nova movimentação
            </Typography>
          </IconButton>
        </Tooltip>
      </Box>
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid #e0e0e0' }}>
        <List disablePadding>
          {movimentacoes.length > 0 ? (
            movimentacoes.map((m, idx) => (
              <React.Fragment key={m.id}>
                <ListItem 
                  onClick={() => abrirEdicao(m)}
                  sx={{ py: 1, px: { xs: 1, sm: 2 }, '&:hover': { bgcolor: '#f5f5f5', cursor: 'pointer' }, display: 'flex', alignItems: 'flex-start', gap: 1 }}
                >
                  <Box sx={{ minWidth: 24, color: m.valor > 0 ? 'green' : 'red', mr: 0.5, display: 'flex', alignItems: 'center', flexShrink: 0, mt: 0.5 }}>
                    {m.valor > 0 ? <ArrowUpward sx={{ fontSize: 16 }} /> : <ArrowDownward sx={{ fontSize: 16 }} />}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                      <Typography variant="body2" fontWeight="bold" sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
                        {m.titulo || m.descricao || 'Movimentação'}
                      </Typography>
                      <Chip 
                        label={(m.tipo || (m.valor >= 0 ? 'entrada' : 'saida')) === 'entrada' ? 'Entrada' : 'Saída'} 
                        size="small" 
                        sx={{ height: 18, fontSize: '0.6rem', bgcolor: m.valor > 0 ? '#e8f5e9' : '#fff3e0', color: m.valor > 0 ? '#2e7d32' : '#ed6c02' }}
                      />
                    </Box>
                    <Typography variant="caption" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' }, color: 'text.secondary' }}>
                      {formatDateSafe(m.data)}
                    </Typography>
                  </Box>
                  <Typography variant="body2" fontWeight="bold" color={m.valor > 0 ? 'green' : 'red'} sx={{ fontSize: { xs: '0.85rem', sm: '0.95rem' }, whiteSpace: 'nowrap', ml: 1 }}>
                    {m.valor > 0 ? '+' : ''} R$ {Math.abs(m.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </Typography>
                </ListItem>
                {idx < movimentacoes.length - 1 && <Divider />}
              </React.Fragment>
            ))
          ) : (
            <Typography variant="body2" color="textSecondary" sx={{ p: 3, textAlign: 'center', fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
              Nenhuma movimentação registrada
            </Typography>
          )}
        </List>
      </Paper>

      {/* POPUP DE NOVA MOVIMENTAÇÃO */}
      <Dialog open={openNovaMov} onClose={fecharNovaMov} fullWidth maxWidth="sm">
        <DialogTitle sx={{ bgcolor: '#1a237e', color: 'white', display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'bold' }}>
          <Add fontSize="small" /> Nova Movimentação
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={8}>
              <TextField 
                fullWidth 
                size="small" 
                label="Título da Movimentação" 
                placeholder="Ex: Salário, Aluguel, Parcela do carro..." 
                value={novaMovimentacao.titulo}
                onChange={(e) => setNovaMovimentacao({...novaMovimentacao, titulo: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField 
                select
                fullWidth 
                size="small" 
                label="Tipo"
                value={novaMovimentacao.tipo}
                onChange={(e) => {
                  setNovaMovimentacao({
                    ...novaMovimentacao,
                    tipo: e.target.value,
                    titulo: e.target.value === 'entrada' ? '' : novaMovimentacao.titulo,
                    categoriaId: ''
                  });
                }}
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value="entrada">Entrada</MenuItem>
                <MenuItem value="saida">Saída</MenuItem>
              </TextField>
            </Grid>

            {novaMovimentacao.tipo === 'saida' && (
              <Grid item xs={12}>
                <TextField 
                  select
                  fullWidth 
                  size="small" 
                  label="Categoria (Opcional)"
                  value={novaMovimentacao.categoriaId}
                  onChange={(e) => {
                    const categoriaId = e.target.value;
                    const categoriaSelecionada = despesas.find((d) => d.id === categoriaId);

                    const pickValorDespesa = (despesa) => {
                      if (!despesa) return '';
                      if (despesa.parcelas && despesa.parcelas.length > 0) {
                        const primeiraPendente = despesa.parcelas.find(p => !p.paga) || despesa.parcelas[0];
                        return primeiraPendente?.valor ?? '';
                      }
                      return despesa.valor ?? '';
                    };

                    const valorDespesa = pickValorDespesa(categoriaSelecionada);
                    setNovaMovimentacao({
                      ...novaMovimentacao,
                      categoriaId,
                      titulo: categoriaSelecionada ? categoriaSelecionada.nome : novaMovimentacao.titulo,
                      valor: valorDespesa !== '' ? formatarMoeda(valorDespesa.toString()) : novaMovimentacao.valor,
                      data: categoriaSelecionada?.parcelas?.length
                        ? (categoriaSelecionada.parcelas.find(p => !p.paga)?.dataParcela || categoriaSelecionada.parcelas[0].dataParcela || novaMovimentacao.data)
                        : novaMovimentacao.data
                    });
                  }}
                  SelectProps={{ displayEmpty: true }}
                  InputLabelProps={{ shrink: true }}
                >
                  <MenuItem value="">
                    <em>Nenhuma</em>
                  </MenuItem>
                  <MenuItem value="__hint" disabled>
                    <em>Pré-preencher com categoria</em>
                  </MenuItem>
                  {despesas.map((d) => (
                    <MenuItem key={d.id} value={d.id}>{d.nome}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
            
            <Grid item xs={6}>
              <TextField 
                fullWidth 
                size="small" 
                label="Valor"
                value={novaMovimentacao.valor}
                onChange={(e) => setNovaMovimentacao({...novaMovimentacao, valor: formatarMoeda(e.target.value)})}
                placeholder="0,00"
                InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField 
                fullWidth 
                size="small" 
                type="date"
                label="Data"
                value={novaMovimentacao.data}
                onChange={(e) => setNovaMovimentacao({...novaMovimentacao, data: e.target.value})}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField 
                fullWidth 
                size="small" 
                label="Descrição Detalhada (Opcional)" 
                placeholder="Adicione mais detalhes sobre a movimentação aqui..." 
                value={novaMovimentacao.descricao}
                onChange={(e) => setNovaMovimentacao({...novaMovimentacao, descricao: e.target.value})}
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={fecharNovaMov}>Cancelar</Button>
          <Button 
            variant="contained" 
            onClick={adicionarMovimentacao}
            sx={{ bgcolor: '#1a237e' }}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* MODAL DE EDIÇÃO */}
      <Dialog open={openEdit} onClose={() => { setOpenEdit(false); setMovSelecionada(null); }} fullWidth maxWidth="sm">
        <DialogTitle sx={{ bgcolor: '#1a237e', color: 'white', display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'bold' }}>
          <Edit fontSize="small" /> Editar Movimentação
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {movSelecionada && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: '#1a237e' }}>
                Informações da Movimentação
              </Typography>
              <Box sx={{ p: 1.5, mb: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>Banco</Typography>
                <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>{banco?.nome}</Typography>
                
                <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>Tipo</Typography>
                <Chip 
                  label={movSelecionada.valor > 0 ? 'Entrada' : 'Saída'} 
                  size="small" 
                  sx={{ mb: 1, height: 20, bgcolor: movSelecionada.valor > 0 ? '#e8f5e9' : '#fff3e0', color: movSelecionada.valor > 0 ? '#2e7d32' : '#ed6c02' }}
                />
                
                <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem', display: 'block' }}>Valor Atual</Typography>
                <Typography variant="body2" fontWeight="bold" color={movSelecionada.valor > 0 ? 'green' : 'red'}>
                  R$ {Math.abs(movSelecionada.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </Typography>
              </Box>
            </Box>
          )}
          
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: '#1a237e' }}>
            Editar Detalhes
          </Typography>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField 
                fullWidth 
                label="Título" 
                value={editTitulo}
                onChange={(e) => setEditTitulo(e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField 
                fullWidth 
                type="date"
                label="Data"
                value={editData}
                onChange={(e) => setEditData(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField 
                fullWidth 
                label="Valor"
                value={editValor}
                onChange={(e) => setEditValor(formatarMoeda(e.target.value))}
                InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField 
                fullWidth 
                multiline
                rows={3}
                label="Descrição Detalhada" 
                placeholder="Adicione detalhes sobre essa movimentação..." 
                value={editDescricao}
                onChange={(e) => setEditDescricao(e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button 
            onClick={() => deletarMovimentacao(movSelecionada)}
            color="error"
            variant="outlined"
            sx={{ mr: 'auto' }}
            startIcon={<Delete fontSize="small" />}
          >
            Deletar
          </Button>
          <Button onClick={() => { setOpenEdit(false); setMovSelecionada(null); }}>
            Cancelar
          </Button>
          <Button 
            onClick={salvarEdicao}
            variant="contained"
            sx={{ bgcolor: '#1a237e' }}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* DIALOG PARA EDITAR NOME DO BANCO */}
      <Dialog open={openEditBanco} onClose={() => setOpenEditBanco(false)}>
        <DialogTitle sx={{ fontSize: '1.1rem' }}>Editar Nome da Conta</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Nome da Conta"
            value={novoNomeBanco}
            onChange={(e) => setNovoNomeBanco(e.target.value)}
            sx={{ mt: 1 }}
            onKeyPress={(e) => e.key === 'Enter' && salvarEdicaoBanco()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditBanco(false)}>Cancelar</Button>
          <Button 
            onClick={salvarEdicaoBanco}
            variant="contained"
            disabled={!novoNomeBanco.trim()}
            sx={{ bgcolor: '#1a237e' }}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* DIALOG PARA CONFIRMAR EXCLUSÃO DO BANCO */}
      <Dialog open={openDeleteConfirm} onClose={() => setOpenDeleteConfirm(false)}>
        <DialogTitle sx={{ fontSize: '1.1rem' }}>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir esta conta bancária? <strong>Todas as movimentações associadas a ela serão perdidas permanentemente.</strong> Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteConfirm(false)}>Cancelar</Button>
          <Button 
            onClick={confirmDeleteBanco}
            variant="contained"
            color="error"
          >
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default BancoDetalhes;
